import { useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useLang } from "../../context/LanguageContext"
import { TourSpotlight, useTourTargetRects } from "./TourSpotlight"

// ── Step data ────────────────────────────────────────────────────────────────
// Selectors point at real, live game UI (see the `data-tour` / `data-tour-group`
// attributes added to PlayerDashboard, ActionPanel, GameLog, BottomPanel, PlayerHand,
// and BoardPosition) — the tour never renders its own mock art, only spotlights what's
// already on screen. `#own-player-dashboard` scopes the right-panel steps to the local
// human player's own dashboard specifically, never another player's.
const STEPS: { captionKey: string; selectors: string[] }[] = [
  // Right panel — section, then each piece
  { captionKey: "tour.step.section.caption", selectors: ["#own-player-dashboard"] },
  { captionKey: "tour.step.money.caption", selectors: ["#own-player-dashboard [data-tour='pd-money']"] },
  { captionKey: "tour.step.rank.caption", selectors: ["#own-player-dashboard [data-tour='pd-rank']"] },
  { captionKey: "tour.step.businesses.caption", selectors: ["#own-player-dashboard [data-tour='pd-businesses']"] },
  { captionKey: "tour.step.gangsterCount.caption", selectors: ["#own-player-dashboard [data-tour='pd-gangster-count']"] },
  { captionKey: "tour.step.gangsterChips.caption", selectors: ["#own-player-dashboard [data-tour='pd-gangster-chips']"] },
  { captionKey: "tour.step.bank.caption", selectors: ["[data-tour='bank-total']"] },
  { captionKey: "tour.step.log.caption", selectors: ["[data-tour='game-log']"] },
  // Bottom panel
  { captionKey: "tour.step.status.caption", selectors: ["[data-tour='status-message']"] },
  { captionKey: "tour.step.hand.caption", selectors: ["[data-tour='player-hand']", "[data-tour='discard-button']"] },
  // Game board — each a scattered multi-target hole set
  { captionKey: "tour.step.ownSeats.caption", selectors: ["[data-tour-group='own-seats']"] },
  { captionKey: "tour.step.businessMarkers.caption", selectors: ["[data-tour-group='business-markers']"] },
  { captionKey: "tour.step.weaponMarkers.caption", selectors: ["[data-tour-group='weapon-markers']"] },
  { captionKey: "tour.step.drinkMarkers.caption", selectors: ["[data-tour-group='drink-markers']"] },
]

interface BoardTourProps {
  /** Reached the final step's finish control — the only path that marks onboarding seen. */
  onComplete: () => void
  /** Ends the tour immediately at any step — does not mark onboarding seen. */
  onSkip: () => void
}

export function BoardTour({ onComplete, onSkip }: BoardTourProps) {
  const { t } = useLang()
  const [step, setStep] = useState(0)
  const isFirst = step === 0
  const isLast = step === STEPS.length - 1
  const rects = useTourTargetRects(STEPS[step].selectors)

  return (
    <>
      {/* Blocks interaction with the real board underneath — same full pause as the
          slide deck, just with the real page visible through the spotlight holes. */}
      <div className="fixed inset-0" style={{ zIndex: 54 }} />
      <TourSpotlight rects={rects} />

      {/* Caption/control dock — fixed bottom-center avoids per-step collision logic
          with scattered board targets that could be anywhere on screen. */}
      <div
        className="fixed left-1/2 bottom-6 -translate-x-1/2 w-full max-w-md px-4"
        style={{ zIndex: 56 }}
      >
        <div className="bg-[#110806] border border-[#C9A84C]/50 rounded-lg flex flex-col shadow-2xl">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-[#C9A84C]/25">
            <span className="text-[10px] uppercase tracking-[0.3em] font-serif text-[#C9A84C]">
              {step + 1} / {STEPS.length}
            </span>
            <button
              onClick={onSkip}
              className="text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1 rounded"
            >
              {t("tour.nav.skip")}
            </button>
          </div>

          <div className="px-5 py-4">
            <p className="text-zinc-300 text-sm leading-relaxed text-center">{t(STEPS[step].captionKey)}</p>
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-[#C9A84C]/20 gap-3">
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={isFirst}
              className="flex items-center gap-1.5 text-sm font-serif uppercase tracking-wider transition-colors px-3 py-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ color: "#C9A84C" }}
            >
              <ChevronLeft className="w-4 h-4" />
              {t("tour.nav.back")}
            </button>

            <button
              onClick={isLast ? onComplete : () => setStep((s) => s + 1)}
              className="flex items-center gap-1.5 text-sm font-serif uppercase tracking-wider transition-opacity px-3 py-1.5 rounded"
              style={{ color: "#F5AC0E" }}
            >
              {isLast ? t("tour.cta.finish") : t("tour.nav.next")}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
